// pages/events/EditEventPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Container, Grid, Typography, Button, TextField, Card, CardMedia,
    IconButton, CircularProgress, Checkbox, FormControlLabel, FormHelperText, Box,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AddPhotoAlternate, Delete } from "@mui/icons-material";
import { DefaultPage } from "../../../layouts/default-page";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
    useGetEventByIdQuery,
    useUpdateEventMutation,
} from "../../../services/updates.query";
import { Notify } from "../../../layouts/notify";

const MAX_IMAGES = 4;

const schema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    content: Yup.string().required("Content is required"),
    date: Yup.date().required("Date is required"),
    imagesNew: Yup.array().max(MAX_IMAGES, `Maximum of ${MAX_IMAGES} images allowed`),
    hasLink: Yup.boolean(),
    linkAlias: Yup.string().when("hasLink", {
        is: true, then: (s) => s.required("Display Text is required"), otherwise: (s) => s.notRequired(),
    }),
    linkUrl: Yup.string().when("hasLink", {
        is: true, then: (s) => s.required("Link URL is required").url("Invalid URL format"), otherwise: (s) => s.notRequired(),
    }),
});

const EditEventPage: React.FC = () => {
    const { id = "" } = useParams();
    const navigate = useNavigate();

    const { data: event, isLoading } = useGetEventByIdQuery(id);
    const [updateEvent, { isLoading: saving }] = useUpdateEventMutation();

    // existing images + keep ids
    const [keepImageIds, setKeepImageIds] = useState<string[]>([]);
    const [existingImages, setExistingImages] = useState<any[]>([]);
    const existingLink = useMemo(
        () => event?.attachments?.find((a: any) => a.type === "FILE"),
        [event]
    );

    // NEW image files + stable preview URLs
    const [newImages, setNewImages] = useState<File[]>([]);
    const [newImageUrls, setNewImageUrls] = useState<string[]>([]);
    const [fileKey, setFileKey] = useState(0); // reset <input type="file">

    useEffect(() => {
        if (event?.attachments) {
            const imgs = event.attachments.filter((a: any) => a.type === "IMAGE");
            setExistingImages(imgs);
            setKeepImageIds(imgs.map((a: any) => a.id));
        }
    }, [event]);

    // Build/rebuild preview URLs when newImages changes (and clean up old)
    useEffect(() => {
        newImageUrls.forEach((u) => URL.revokeObjectURL(u)); // revoke previous first
        const urls = newImages.map((f) => URL.createObjectURL(f));
        setNewImageUrls(urls);
        return () => {
            urls.forEach((u) => URL.revokeObjectURL(u));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newImages]);

    if (isLoading || !event) {
        return (
            <DefaultPage title="Edit Event">
                <Container maxWidth="md"><CircularProgress /></Container>
            </DefaultPage>
        );
    }

    return (
        <DefaultPage title="Edit Event">
            <Container maxWidth="md">
                <Formik
                    initialValues={{
                        title: event.title || "",
                        content: event.description || "",
                        date: dayjs(event.datetime).toDate(),
                        imagesNew: [] as File[],       // we’ll mirror this with local newImages
                        hasLink: !!existingLink,
                        linkAlias: existingLink?.alias || "",
                        linkUrl: existingLink?.file_url || "",
                    }}
                    validationSchema={schema}
                    onSubmit={async (v) => {
                        // Final guard: must have ≥ 1 image total
                        const finalCount = keepImageIds.length + newImages.length;
                        if (finalCount < 1) {
                            Notify("error", "At least one image is required.");
                            return;
                        }

                        try {
                            const linkPayload = v.hasLink
                                ? { id: existingLink?.id, alias: v.linkAlias, url: v.linkUrl }
                                : null;

                            await updateEvent({
                                id,
                                title: v.title,
                                content: v.content,
                                date: v.date,
                                keepImageIds,           // send the kept existing image ids
                                newImages,              // send the newly picked files
                                linkAttachment: linkPayload,
                            }).unwrap();

                            Notify("success", "Event updated.");
                            navigate("/newsupdates/events");
                        } catch (e: any) {
                            Notify("error", e?.message || "Update failed.");
                        }
                    }}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form noValidate>
                            <Grid container spacing={3}>

                                {/* Title */}
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="title"
                                        label="Title"
                                        variant="outlined"
                                        fullWidth
                                        error={touched.title && !!errors.title}
                                        helperText={<ErrorMessage name="title" />}
                                    />
                                </Grid>

                                {/* Content */}
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="content"
                                        label="Content"
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={6}
                                        error={touched.content && !!errors.content}
                                        helperText={<ErrorMessage name="content" />}
                                    />
                                </Grid>

                                {/* Date */}
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Date"
                                        value={dayjs(values.date)}
                                        onChange={(d) => setFieldValue("date", d?.toDate())}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                error: touched.date && !!errors.date,
                                                helperText: touched.date && typeof errors.date === "string" ? errors.date : "",
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Existing images with keep/remove toggle */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>Existing Images</Typography>
                                    <Grid container spacing={2}>
                                        {existingImages.map((img: any) => {
                                            const kept = keepImageIds.includes(img.id);
                                            return (
                                                <Grid item xs={6} sm={3} key={img.id}>
                                                    <Card sx={{ position: "relative", borderRadius: 0.5 }}>
                                                        <CardMedia component="img" height="140" image={img.file_url} />
                                                        <IconButton
                                                            onClick={() => {
                                                                setKeepImageIds(prev =>
                                                                    prev.includes(img.id)
                                                                        ? prev.filter(x => x !== img.id)
                                                                        : [...prev, img.id]
                                                                );
                                                            }}
                                                            color={kept ? "success" : "error"}
                                                            sx={{
                                                                position: "absolute",
                                                                top: 5,
                                                                right: 5,
                                                                bgcolor: "rgba(255,255,255,.8)",
                                                                borderRadius: 1.5,
                                                            }}
                                                            title={kept ? "Click to remove" : "Click to keep"}
                                                        >
                                                            {kept ? <Delete sx={{ opacity: .3 }} /> : <Delete />}
                                                        </IconButton>
                                                    </Card>
                                                    <Typography variant="caption" sx={{ display: "block", mt: .5 }}>
                                                        {kept ? "Keeping" : "Will remove"}
                                                    </Typography>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ mt: 0.5, color: 'error.main' }}>
                                            Note: If you add new images and keep existing ones, the <strong>first newly added or selected image</strong> will be treated as the <strong>MAIN IMAGE</strong>.
                                        </Typography>
                                    </Grid>
                                </Grid>
                                {/* Upload NEW images (append + preview) */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom>
                                        Upload New Images (Max: {MAX_IMAGES})
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        component="label"
                                        startIcon={<AddPhotoAlternate />}
                                        sx={{ mb: 1 }}
                                        disabled={(keepImageIds.length + newImages.length) >= MAX_IMAGES}
                                    >
                                        Select Images
                                        <input
                                            key={fileKey}               // force-reset input so same file can be picked again
                                            type="file"
                                            hidden
                                            accept="image/*"
                                            multiple
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const files = e.target.files ? Array.from(e.target.files) : [];
                                                if (!files.length) return;

                                                // Enforce MAX_IMAGES across kept + new
                                                const room = Math.max(0, MAX_IMAGES - keepImageIds.length - newImages.length);
                                                const toAdd = files.slice(0, room);
                                                if (toAdd.length < files.length) {
                                                    Notify("error", `Only ${room} more image(s) allowed (max ${MAX_IMAGES}).`);
                                                }

                                                const next = [...newImages, ...toAdd];
                                                setNewImages(next);
                                                setFieldValue("imagesNew", next); // keep Formik in sync (for validation/UI)
                                                setFileKey((k) => k + 1);
                                            }}
                                        />
                                    </Button>

                                    {/* Preview NEW images */}
                                    {newImageUrls.length > 0 && (
                                        <Grid container spacing={2} sx={{ mt: 0 }}>
                                            {newImageUrls.map((url, idx) => (
                                                <Grid item xs={6} sm={3} key={`new-${idx}`}>
                                                    <Card sx={{ position: "relative", borderRadius: 0.5 }}>
                                                        <CardMedia component="img" height="140" image={url} />
                                                        <IconButton
                                                            onClick={() => {
                                                                const copy = [...newImages];
                                                                copy.splice(idx, 1);
                                                                setNewImages(copy);
                                                                setFieldValue("imagesNew", copy);
                                                            }}
                                                            color="error"
                                                            sx={{
                                                                position: "absolute",
                                                                top: 5,
                                                                right: 5,
                                                                bgcolor: "rgba(255,255,255,.7)",
                                                                borderRadius: 1.5,
                                                            }}
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}

                                    {/* Validation hints */}
                                    {touched.imagesNew && typeof (errors as any).imagesNew === "string" && (
                                        <FormHelperText error sx={{ mt: 1 }}>
                                            {(errors as any).imagesNew}
                                        </FormHelperText>
                                    )}
                                    {(keepImageIds.length + newImages.length) === 0 && (
                                        <FormHelperText error sx={{ mt: 1 }}>
                                            At least 1 image is required.
                                        </FormHelperText>
                                    )}
                                </Grid>

                                {/* Link (single) */}
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={values.hasLink}
                                                onChange={(e) => setFieldValue("hasLink", e.target.checked)}
                                            />
                                        }
                                        label="Add Link Attachment"
                                    />
                                </Grid>

                                {values.hasLink && (
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                as={TextField}
                                                name="linkAlias"
                                                label="Display Text"
                                                fullWidth
                                                error={touched.linkAlias && !!errors.linkAlias}
                                                helperText={<ErrorMessage name="linkAlias" />}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                as={TextField}
                                                name="linkUrl"
                                                label="Link URL"
                                                fullWidth
                                                error={touched.linkUrl && !!errors.linkUrl}
                                                helperText={<ErrorMessage name="linkUrl" />}
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {/* Save */}
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="success"
                                        size="large"
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : undefined}
                                        disabled={saving}
                                    >
                                        {saving ? "Saving..." : "Save Changes"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Container>
        </DefaultPage>
    );
};

export default EditEventPage;
